/**
 * Converts a date string to an integer in YYYYMMDD format without any timezone conversion
 * Takes the date at face value from the string representation
 * @param dateStr - The date string to convert (e.g., "01/17/2026", "2026-01-17", or ISO string)
 * @returns The date as an integer in YYYYMMDD format (e.g., 20260117)
 */
export const dateToInteger = (dateStr) => {
    if (!dateStr)
        return 0;
    let year, month, day;
    if (dateStr.includes('/')) {
        // Format: MM/DD/YYYY - parse directly from string
        const parts = dateStr.split('/');
        if (parts.length !== 3)
            return 0;
        month = parseInt(parts[0]);
        day = parseInt(parts[1]);
        year = parseInt(parts[2]);
        if (isNaN(month) || isNaN(day) || isNaN(year))
            return 0;
    }
    else if (dateStr.includes('-')) {
        // Format: YYYY-MM-DD or ISO string - parse the date portion
        const datePart = dateStr.split('T')[0]; // Handle ISO strings with time
        const parts = datePart.split('-');
        if (parts.length !== 3)
            return 0;
        year = parseInt(parts[0]);
        month = parseInt(parts[1]);
        day = parseInt(parts[2]);
        if (isNaN(year) || isNaN(month) || isNaN(day))
            return 0;
    }
    else {
        // Fallback: try to parse as a Date and extract components
        // This uses UTC methods to avoid timezone conversion
        const date = new Date(dateStr);
        if (isNaN(date.getTime()))
            return 0;
        year = date.getUTCFullYear();
        month = date.getUTCMonth() + 1;
        day = date.getUTCDate();
    }
    return year * 10000 + month * 100 + day;
};
/**
 * Gets the current date as an integer in YYYYMMDD format in a specific timezone
 * @param timezone - The timezone to use (e.g., "Asia/Tokyo", "America/New_York")
 * @returns The current date as an integer in YYYYMMDD format (e.g., 20260117)
 */
export const getCurrentDateIntegerInTimezone = (timezone) => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const parts = formatter.formatToParts(now);
    const getPart = (type) => parts.find(part => part.type === type)?.value || '0';
    const year = parseInt(getPart('year'));
    const month = parseInt(getPart('month'));
    const day = parseInt(getPart('day'));
    return year * 10000 + month * 100 + day;
};
/**
 * Returns today's date as a YYYY-MM-DD string based on the user's browser timezone.
 */
export const getTodayDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
/**
 * Checks if a date is in the future relative to the current date in a specific timezone
 * @param dateStr - The date string to check (e.g., "01/17/2026" or ISO string)
 * @param timezone - The timezone to use for comparison (e.g., "Asia/Tokyo", "America/New_York")
 * @returns true if the date is in the future, false otherwise
 */
export const isFutureDateInTimezone = (dateStr, timezone) => {
    if (!dateStr)
        return false;
    const selectedDateInt = dateToInteger(dateStr);
    const currentDateInt = getCurrentDateIntegerInTimezone(timezone);
    return selectedDateInt > currentDateInt;
};
